import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import DataTable from '../components/Tables/DataTable';
import TimesheetForm from '../components/Forms/TimesheetForm';
import { Plus, Play } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';

const Timesheets = () => {
    const { currency } = useAuth();
    const [timesheets, setTimesheets] = useState([]);
    const [projects, setProjects] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingEntry, setEditingEntry] = useState<any>(null);

    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const [timerStartTime, setTimerStartTime] = useState<number | null>(null);
    const [elapsedTime, setElapsedTime] = useState('00:00:00');
    const [showTimerConfirm, setShowTimerConfirm] = useState(false);
    const pauseStartTimeRef = useRef<number | null>(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [timesheetsRes, projectsRes, customersRes] = await Promise.all([
                api.get('/timesheets'),
                api.get('/projects'),
                api.get('/customers?limit=100')
            ]);
            setTimesheets(timesheetsRes.data);
            setProjects(projectsRes.data);
            setCustomers(customersRes.data.customers || []);
        } catch (err) {
            console.error('Error fetching data', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isTimerRunning && timerStartTime && !showTimerConfirm) {
            interval = setInterval(() => {
                const now = Date.now();
                const diff = now - timerStartTime;
                const hours = Math.floor(diff / 3600000);
                const minutes = Math.floor((diff % 3600000) / 60000);
                const seconds = Math.floor((diff % 60000) / 1000);
                setElapsedTime(
                    `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
                );
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isTimerRunning, timerStartTime, showTimerConfirm]);

    const toggleTimer = () => {
        if (!isTimerRunning) {
            setIsTimerRunning(true);
            setTimerStartTime(Date.now());
            setElapsedTime('00:00:00');
        } else {
            pauseStartTimeRef.current = Date.now();
            setShowTimerConfirm(true);
        }
    };

    const confirmStopTimer = () => {
        setShowTimerConfirm(false);
        setIsTimerRunning(false);
        setTimerStartTime(null);
        pauseStartTimeRef.current = null;
        // Open form with pre-filled time
        // Format to HH:MM for the form
        const [hours, minutes] = elapsedTime.split(':');
        setEditingEntry({
            date: new Date().toISOString(),
            timeSpent: `${hours}:${minutes}`,
            projectId: '', // User needs to select
            taskId: '',
            isBillable: true
        });
        setShowForm(true);
    };

    const cancelStopTimer = () => {
        if (timerStartTime && pauseStartTimeRef.current) {
            const pauseDuration = Date.now() - pauseStartTimeRef.current;
            setTimerStartTime(timerStartTime + pauseDuration);
        }
        pauseStartTimeRef.current = null;
        setShowTimerConfirm(false);
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCreate = () => {
        setEditingEntry(null);
        setShowForm(true);
    };

    const handleEdit = (entry: any) => {
        setEditingEntry(entry);
        setShowForm(true);
    };

    const handleDelete = async (entry: any) => {
        if (window.confirm('Are you sure you want to delete this entry?')) {
            try {
                await api.delete(`/timesheets/${entry.id}`);
                fetchData();
            } catch (err) {
                console.error('Error deleting timesheet', err);
            }
        }
    };

    const columns = [
        {
            header: 'Date',
            accessor: (item: any) => new Date(item.date).toLocaleDateString()
        },
        {
            header: 'Project',
            accessor: (item: any) => item.project?.projectName || 'N/A'
        },
        {
            header: 'Customer',
            accessor: (item: any) => item.customer?.displayName || 'N/A'
        },
        {
            header: 'Task',
            accessor: (item: any) => item.task?.taskName || '-'
        },
        { header: 'User', accessor: (item: any) => item.user?.name || 'Unknown' },
        { header: 'Time', accessor: 'timeSpent' },
        {
            header: 'Total Amount',
            accessor: (item: any) => {
                const project = item.project;
                if (!project) return formatCurrency(0, currency);

                if (project.billingMethod === 'Fixed Cost') {
                    return formatCurrency(Number(project.hourlyRate || 0), currency);
                }

                if (project.billingMethod === 'Non-Billable') {
                    return formatCurrency(0, currency);
                }

                const rate = project.hourlyRate;
                if (!rate || !item.timeSpent) return formatCurrency(0, currency);
                const [hours, minutes] = item.timeSpent.split(':').map(Number);
                if (isNaN(hours) || isNaN(minutes)) return formatCurrency(0, currency);
                const totalHours = hours + (minutes / 60);
                return formatCurrency(totalHours * Number(rate), currency);
            }
        },
    ];

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">All Timesheets</h1>
                <div className="flex gap-2">
                    <button
                        onClick={toggleTimer}
                        className={`flex items-center gap-2 border px-4 py-2 rounded-md transition ${isTimerRunning
                            ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
                            : 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100'
                            }`}
                    >
                        {isTimerRunning ? (
                            <>
                                <div className="w-3 h-3 bg-red-600 rounded-sm animate-pulse" />
                                Stop Timer ({elapsedTime})
                            </>
                        ) : (
                            <>
                                <Play size={18} />
                                Start Timer
                            </>
                        )}
                    </button>
                    <button
                        onClick={handleCreate}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
                    >
                        <Plus size={18} />
                        New Log Entry
                    </button>
                </div>
            </div>

            <DataTable
                data={timesheets}
                columns={columns}
                total={timesheets.length}
                page={1}
                limit={100}
                onPageChange={() => { }}
                onEdit={handleEdit}
                onDelete={handleDelete}
                loading={loading}
            />

            {showForm && (
                <TimesheetForm
                    onClose={() => setShowForm(false)}
                    onSuccess={() => {
                        setShowForm(false);
                        fetchData();
                    }}
                    entry={editingEntry}
                    projects={projects}
                    customers={customers}
                />
            )}

            {showTimerConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
                        <h3 className="text-lg font-bold text-gray-800 mb-2">Stop Timer?</h3>
                        <p className="text-gray-600 mb-6">
                            Do you want to stop the timer and log this time entry?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={cancelStopTimer}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition"
                            >
                                Continue Timing
                            </button>
                            <button
                                onClick={confirmStopTimer}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
                            >
                                Stop & Log
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Timesheets;
